import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "../utils/toast";
import { useParams, useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Header from "../header/Header";
import Title from "../elementos/Title";
import SideForm from "../elementos/SideForm";
import AddCardPanel from "./AddCardPanel";
import Loader from "../loader/Loader";
import texts from "../data/texts";
import { accessAPI, logout } from "../utils/fetchFunctions";
import BinderEditor from "./BinderEditor";
import BoxEditor from "./BoxEditor";
import EditionEditor from "./EditionEditor";
import { withDuplicate, withoutPlacement } from "./optimistic";

const TYPE_LABELS = {
  binder: texts.BINDER,
  sorted_box: texts.SORTED_BOX,
  unsorted_box: texts.UNSORTED_BOX,
  edition_box: texts.EDITION_BOX,
};

const STATE_LABELS = {
  for_sale: texts.STATE_FOR_SALE,
  retired: texts.STATE_RETIRED,
  released: texts.STATE_RELEASED,
  returning: texts.STATE_RETURNING,
};

// One container, opened — the shop's view, same editors as the customer's.
//
// EVERY container opens: a binder is pages of pockets, a sorted box is an
// ordered list, an unsorted box an alphabetical one. Editing is what is gated
// (`unit.editable`, from the API): the store adds, removes and sorts cards
// only in its OWN furniture. A customer's container is displayed and sold
// from, never restocked or reorganized behind their back — every change to
// their cards goes through a flow they see: a sale, a withdrawal, a return.
export default function StorageDetail() {
  const { storageId } = useParams();
  const navigate = useNavigate();
  const [loader, setLoader] = useState(true);
  const [unit, setUnit] = useState(null);
  const [leaving, setLeaving] = useState(false);
  // Whether the add-a-card sidebar is slid out.
  const [adding, setAdding] = useState(false);
  // The card whose version is being changed — opens the add-card panel in
  // change mode, pre-searched for that card.
  const [changing, setChanging] = useState(null);
  // The hidden file input behind the "Importar de ManaBox" Title button.
  const importRef = useRef(null);
  // True from the moment a file is picked until the API answers — a scan of
  // a full binder takes a few seconds, and a page that says nothing reads
  // as a click that did nothing.
  const [importing, setImporting] = useState(false);
  // The element the Title's actions menu hangs from, while it is open.
  const [actionsAnchor, setActionsAnchor] = useState(null);
  // An edition box's import reports, once it finishes, the cards it could
  // not file and why — a list, not a count, because each one is a card the
  // shop has to deal with by hand.
  const [importReport, setImportReport] = useState(null);
  // Bumped after an edition import so the checklist re-reads its quantities.
  const [editionVersion, setEditionVersion] = useState(0);

  // Read the picked CSV and hand it to the API, which maps rows to pockets
  // (empty lines skip one) and keeps the file's condition and language.
  // Comes back as text and leaves as a file: the browser has no other way to
  // save something fetched with an Authorization header.
  function exportCollection() {
    setActionsAnchor(null);
    accessAPI(
      "GET",
      `storage/${unit.id}/export`,
      null,
      ({ csv }) => {
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = `${unit.name}.csv`;
        link.click();
        // Not straight away: Safari cancels a download whose URL goes first.
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      },
      (response) => toast(response.message)
    );
  }

  async function importCollection(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !unit) return;
    setImporting(true);
    const csv = await file.text();
    accessAPI(
      "POST",
      `storage/${unit.id}/import`,
      { csv },
      (result) => {
        setImporting(false);
        if (unit.type === "edition_box") {
          setEditionVersion((v) => v + 1);
          load();
          if (result.errors.length > 0) {
            setImportReport(result);
          } else {
            toast(`${result.added}${texts.IMPORT_DONE_CARDS}`, "success");
          }
          return;
        }
        let message = `${result.added}${texts.IMPORT_DONE_CARDS}`;
        if (result.skipped > 0)
          message += ` · ${result.skipped}${texts.IMPORT_SKIPPED}`;
        if (result.errors.length > 0)
          message += ` · ${result.errors.length}${texts.IMPORT_ERRORS}`;
        toast(message, result.errors.length ? undefined : "success");
        load();
      },
      (response) => {
        setImporting(false);
        toast(response.message);
      },
      // A big scan legitimately takes a while — the default timeout would
      // declare failure while the server is still happily importing.
      { timeout: 180000 }
    );
  }

  const load = useCallback(() => {
    accessAPI(
      "GET",
      `storage/${storageId}`,
      null,
      (response) => {
        setUnit(response);
        setLoader(false);
      },
      (response) => {
        toast(response.message);
        if (response.status === 401) {
          logout();
          navigate("/login");
        } else {
          navigate("/storage");
        }
      }
    );
  }, [storageId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // Every mutation re-reads the container rather than patching state in place.
  // Depths, sequences and stack membership all shift when a card moves, and
  // guessing the new arrangement client-side is how a view starts disagreeing
  // with what is actually stored.
  const after = (response) => load();
  const onError = (response) => toast(response.message);

  const move = (placementid, position) =>
    accessAPI(
      "PUT",
      `storage/placement/${placementid}/position`,
      position,
      after,
      // A failed move must also reload: the editor already shows the card in
      // the pocket it was dropped on, and only fresh server data puts it
      // back where it really is.
      (response) => {
        toast(response.message);
        load();
      }
    );

  // Optimistic: the copy appears the moment the button is clicked, where the
  // API will put it, and the reload swaps in its real id behind the scenes.
  // On failure the copy vanishes again, with the API's reason in a toast.
  // A provisional (negative) id cannot be duplicated — the server does not
  // know that copy yet.
  const duplicate = (placementid) => {
    if (placementid < 0) return;
    const tempid = -Date.now();
    setUnit((u) => withDuplicate(u, placementid, tempid));
    accessAPI(
      "POST",
      `storage/placement/${placementid}/duplicate`,
      null,
      after,
      (response) => {
        toast(`${texts.DUPLICATE_FAILED} ${response.message}`);
        setUnit((u) => withoutPlacement(u, tempid));
      }
    );
  };

  // Shift every card on one binder page a pocket ahead or back; the edge
  // stack is kicked to the stand-by area by the API.
  // Reorder the stack inside one pocket — the dialog sends the whole new
  // order, front (visible) card first.
  const reorderPocket = (page, pocket, placementids) =>
    accessAPI(
      "PUT",
      `storage/${unit.id}/pocket/order`,
      { page, pocket, placementids },
      after,
      onError
    );

  // Swap ONE copy's printing/finish — and its condition and language — for
  // the chosen ones; the placement keeps its exact spot. Closes the change
  // sidebar on success.
  const changeVersion = (version, variant, done, grade = {}) =>
    accessAPI(
      "PUT",
      `storage/placement/${changing.placementid}/version`,
      { scryfallid: version.scryfallid, variant, ...grade },
      () => {
        done();
        setChanging(null);
        toast(texts.VERSION_CHANGED, "success");
        load();
      },
      (response) => {
        done();
        toast(response.message);
      }
    );

  const shiftPage = (page, frompocket, direction) =>
    accessAPI(
      "POST",
      `storage/${unit.id}/page/${page}/shift`,
      { frompocket, direction },
      after,
      onError
    );

  // Optimistic like duplicate: the card leaves the screen immediately, and a
  // failure brings it back by reloading — only fresh server data knows where
  // it really was.
  const remove = (placementid) => {
    if (placementid < 0) return;
    setUnit((u) => withoutPlacement(u, placementid));
    accessAPI(
      "DELETE",
      `storage/placement/${placementid}`,
      null,
      after,
      (response) => {
        toast(`${texts.REMOVE_FAILED} ${response.message}`);
        load();
      }
    );
  };

  const reorder = (placementids) =>
    accessAPI(
      "PUT",
      `storage/${storageId}/order`,
      { placementids },
      after,
      onError
    );

  const standbyCount = unit?.standby?.length ?? 0;
  // An edition box is edited as a checklist of quantities, so it shares none
  // of the add / import / arrange controls the other containers have.
  const isEdition = unit?.type === "edition_box";

  // Leaving with cards still in stand-by throws them away — a card with nowhere
  // to live is exactly what this model does not allow. Warned about first,
  // because it is destructive and not obvious.
  function leave() {
    if (unit?.arrangeable && standbyCount > 0) {
      setLeaving(true);
      return;
    }
    navigate("/storage");
  }

  function discardAndLeave() {
    accessAPI(
      "POST",
      `storage/${storageId}/discard-standby`,
      null,
      () => navigate("/storage"),
      (response) => {
        setLeaving(false);
        toast(response.message);
      }
    );
  }

  return (
    <div>
      <Header showMenu={true} loggedIn={true} />
      <div className="content">
        {loader && <Loader color="blue" />}
        {!loader && unit && (
          <>
            <Title
              // Through leave(), not a bare navigate: the stand-by discard
              // warning has to fire from the arrow too.
              onBack={leave}
              title={unit.name}
              subtitle={`${unit.cardcount} ${texts.CARDS}`}
              tags={[
                TYPE_LABELS[unit.type],
                // An edition box is only ever one set; saying which is more
                // use on this page than saying it again in the title.
                ...(unit.cardsetname ? [unit.cardsetname] : []),
                unit.owner ? unit.owner.name : texts.SHOP,
                {
                  label: STATE_LABELS[unit.state],
                  color: unit.forsale ? "success" : undefined,
                },
              ]}
              buttons={
                // Adding follows physical possession, like arranging: a
                // customer walking in with more cards for their consigned
                // binder hands them over the counter. An edition box has no
                // per-card add — its checklist is how cards go in and out.
                !isEdition && unit.arrangeable
                  ? [{ label: texts.ADD_CARD, onClick: () => setAdding(true) }]
                  : []
              }
            >
              <Button
                size="small"
                endIcon={<ArrowDropDownIcon />}
                onClick={(e) => setActionsAnchor(e.currentTarget)}
              >
                {texts.ACTIONS}
              </Button>
              <Menu
                anchorEl={actionsAnchor}
                open={Boolean(actionsAnchor)}
                onClose={() => setActionsAnchor(null)}
              >
                {/* Importing follows the same rule as adding, and an edition
                    box does import, once its set is known: the file is read
                    by card name only and filed into that set. Exporting
                    changes nothing, so it is always on offer. */}
                {(isEdition
                  ? unit.editable && unit.cardsetcode
                  : unit.arrangeable) && (
                  <MenuItem
                    disabled={importing}
                    onClick={() => {
                      setActionsAnchor(null);
                      importRef.current?.click();
                    }}
                  >
                    {texts.IMPORT_COLLECTION}
                  </MenuItem>
                )}
                <MenuItem onClick={exportCollection}>{texts.EXPORT_CSV}</MenuItem>
              </Menu>
            </Title>

            {/* Working, said out loud: the import processes card by card
                and a big scan takes seconds. */}
            {importing && (
              <Alert
                icon={<CircularProgress size={18} />}
                severity="info"
                sx={{ mb: 2 }}
              >
                {texts.IMPORTING_COLLECTION}
              </Alert>
            )}

            {/* The ManaBox button is a file picker: the browse dialog IS the
                interaction, so the input hides and the Title button clicks
                it. Resetting value lets the same file be picked twice. */}
            <input
              ref={importRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={importCollection}
            />

            {/* Says why controls are absent, rather than leaving them to be
                puzzled over. A customer's container in the shop's hands can be
                rearranged but not restocked; one in the customer's hands is
                untouchable. */}
            {!unit.arrangeable && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {texts.STORAGE_LOCKED}
              </Alert>
            )}
            {unit.arrangeable && !unit.editable && !isEdition && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {texts.STORAGE_ARRANGE_ONLY}
              </Alert>
            )}

            {isEdition ? (
              <EditionEditor
                unit={unit}
                editable={unit.editable}
                reloadKey={editionVersion}
                // The header above shows the container's card count, and a
                // quantity change moves it.
                onChanged={load}
              />
            ) : unit.type === "binder" ? (
              <BinderEditor
                unit={unit}
                arrange={unit.arrangeable}
                mutate={unit.editable}
                withdrawable={false}
                onMove={move}
                onDuplicate={duplicate}
                onRemove={remove}
                onShiftPage={shiftPage}
                onReorderPocket={reorderPocket}
                onEditVersion={unit.editable ? setChanging : null}
              />
            ) : (
              <>
                {!unit.cards?.length && (
                  <Alert severity="info">{texts.CONTAINER_EMPTY}</Alert>
                )}
                {unit.type === "unsorted_box" && unit.cards?.length > 0 && (
                  <Alert severity="info" sx={{ mb: 1.5 }}>
                    {texts.UNSORTED_HINT}
                  </Alert>
                )}
                <BoxEditor
                  unit={unit}
                  arrange={unit.arrangeable}
                  mutate={unit.editable}
                  withdrawable={false}
                  onDuplicate={duplicate}
                  onRemove={remove}
                  onReorder={reorder}
                  onEditVersion={unit.editable ? setChanging : null}
                />
              </>
            )}
          </>
        )}
      </div>

      {/* Adding stays open across adds: filling a container is a run of
          them, and every add re-reads the container so the page behind is
          already current when the sidebar closes. */}
      {unit && (
        <SideForm
          open={adding}
          onClose={() => setAdding(false)}
          title={texts.ADD_CARD}
          width={720}
        >
          <AddCardPanel unit={unit} onAdded={load} />
        </SideForm>
      )}

      {/* Version change rides the same wide panel, opened in change mode:
          already searched for the card, one Elegir per printing. */}
      {unit && (
        <SideForm
          open={Boolean(changing)}
          onClose={() => setChanging(null)}
          title={texts.CHANGE_VERSION}
          width={720}
        >
          <AddCardPanel
            unit={unit}
            onAdded={load}
            changeTarget={changing}
            onChangeVersion={changeVersion}
          />
        </SideForm>
      )}

      <Dialog
        open={Boolean(importReport)}
        onClose={() => setImportReport(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{texts.EDITION_IMPORT_REPORT_TITLE}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            {importReport?.added ?? 0}
            {texts.IMPORT_DONE_CARDS}
            {texts.EDITION_IMPORT_REPORT_INTRO}
          </DialogContentText>
          <List dense disablePadding>
            {importReport?.errors.map((error, index) => (
              <ListItem key={index} disableGutters>
                <ListItemText
                  primary={
                    error.name
                      ? `${error.quantity ? `${error.quantity}x ` : ""}${error.name}`
                      : `${texts.EDITION_IMPORT_LINE}${error.line}`
                  }
                  secondary={
                    texts.EDITION_IMPORT_REASONS[error.reason] ?? error.reason
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setImportReport(null)}>
            {texts.EDITION_IMPORT_REPORT_CLOSE}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={leaving} onClose={() => setLeaving(false)}>
        <DialogTitle>{texts.STANDBY_DISCARD_TITLE}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {texts.STANDBY_DISCARD_1}
            {standbyCount}
            {texts.STANDBY_DISCARD_2}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setLeaving(false)}>
            {texts.STANDBY_KEEP_EDITING}
          </Button>
          <Button color="error" onClick={discardAndLeave}>
            {texts.STANDBY_DISCARD_CONFIRM}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
